import { useState } from "react";
import {
  useLoaderData,
  useNavigate
} from "react-router-dom";
import ConfirmationModal from "../_Shared/ConfirmationModal.js";
import { ROLES } from '../../components/_Shared/navigation';
import { fetchClient } from '../../util/fetchClient.js';

const pageLimit = 10;

const fetchUsers = async ({ offset=0, limit=pageLimit }) => {
  const { body: users } = await fetchClient(`/v1/users?offset=${offset}&limit=${limit}`);
  return { users: users.data };
};

export const loader = () => fetchUsers({ offset: 0, limit: pageLimit });

const classNames = (...classes) => {
  return classes.filter(Boolean).join(' ')
};

const translateRole = role => {
  switch (role) {
    case ROLES.ADMIN:
      return 'Administrador';
    case ROLES.RECEPCIONIST:
      return 'Recepcionista';
    default:
      return role;
  }
}

export default function Users() {
  const loaderData = useLoaderData();

  const navigate = useNavigate();
  const [selectedUser, setSelectedUser] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [lastPage, setLastPage] = useState(Infinity);
  const [currentPage, setCurrentPage] = useState(1);
  const [users, setUsers] = useState(loaderData.users);

  const newButtonClick = () => navigate('/users/new');

  const editButtonClick = (id) => navigate(`/users/edit/${id}`);

  const deleteButtonClick = async (user) => {
    setSelectedUser(user);
    setShowDeleteModal(true);
  };

  const deleteHandler = async () => {
    await fetchClient(`/v1/users/${selectedUser.id}`, { method: 'DELETE' });
    setShowDeleteModal(false);
    window.location.reload();
  };

  const cancelHandler = () => {
    setShowDeleteModal(false);
  };


  const isLastPage = () => lastPage <= currentPage || users.length < pageLimit || users.length === 0;

  const handlePreviousPage = async () => {
    if (currentPage === 1) {
      return;
    }

    const newPage = currentPage - 1;
    const pageOffset = newPage * pageLimit - pageLimit;
    const { users: updatedUsers } = await fetchUsers({ offset: pageOffset, limit: pageLimit });
    setCurrentPage(currentPage - 1);
    setUsers(updatedUsers);
    setLastPage(Infinity);
  };

  const handleNextPage = async () => {
    if (isLastPage()) {
      return;
    }

    const newPage = currentPage + 1;
    const pageOffset = newPage * pageLimit - pageLimit;
    const { users: updatedUsers } = await fetchUsers({ offset: pageOffset, limit: pageLimit });
    if (updatedUsers.length) {
      setCurrentPage(newPage);
      setUsers(updatedUsers);
      setLastPage(Infinity);
    } else {
      setLastPage(currentPage);
    }
  };

  return (
    <div className="px-4 sm:px-6 lg:px-8">
      { showDeleteModal && ( <ConfirmationModal deleteHandler={deleteHandler} cancelHandler={cancelHandler}/> ) }

      <div className="sm:flex sm:items-center">
        <div className="sm:flex-auto">
          <div className="order-last flex w-full gap-x-8 text-sm font-semibold leading-6 sm:order-none sm:w-auto">
            <h1 className="mt-3 text-base font-semibold leading-6 text-gray-900 -mr-5">Lista de Usuarios</h1>
          </div>
        </div>
        <div className="mt-4 sm:ml-16 sm:mt-0 sm:flex-none">
          <button
            type="button" 
            className="block rounded-md bg-indigo-600 px-3 py-2 text-center text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
            onClick={newButtonClick} >
            Nuevo
          </button>
        </div>
      </div>

      <div className="mt-5 flow-root">
        <div className="-mx-4 -my-2 sm:-mx-6 lg:-mx-8">
          <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
            <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 sm:rounded-lg">
              <table className="min-w-full divide-y divide-gray-300">
                <thead className="bg-gray-50">
                  <tr>
                    <th
                      scope="col"
                      className="top-0 z-10 py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6"
                    >
                      Nombre
                    </th>
                    <th
                      scope="col"
                      className="top-0 z-10 px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                    >
                      Email
                    </th>
                    <th
                      scope="col"
                      className="top-0 z-10 px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                    >
                      Rol
                    </th>
                    <th
                      scope="col"
                      className="top-0 z-10 px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                    >
                      Registro
                    </th>
                    <th
                      scope="col"
                      className="top-0 z-10 px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                    >
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 bg-white">
                  {users.map((user, _userIdx) => (
                    <tr key={user.id}>
                      <td
                        className={classNames(
                          'whitespace-nowrap py-3 pl-2 pr-3 text-sm font-medium text-gray-900 sm:pl-6'
                        )}
                      >
                        {user.first_name} {user.last_name}
                      </td>
                      <td
                        className={classNames(
                          'whitespace-nowrap px-3 py-3 text-sm text-gray-500'
                        )}
                      >
                        {user.email}
                      </td>
                      <td
                        className={classNames(
                          'whitespace-nowrap px-3 py-3 text-sm text-gray-500'
                        )}
                      >
                        {translateRole(user.role)}
                      </td>
                      <td
                        className={classNames(
                          'whitespace-nowrap px-3 py-3 text-sm text-gray-500'
                        )}
                      >
                        {new Date(user.created_at).toLocaleDateString('es-MX')}
                      </td>
                      <td
                        className={classNames(
                          'relative whitespace-nowrap py-3 text-left font-medium pr-1'
                        )}
                      >
                        <button type="button" className="text-indigo-600 hover:text-indigo-900" onClick={() => editButtonClick(user.id)}>Editar</button>
                        <button type="button" hidden={user.role === ROLES.ADMIN} className="text-rose-600 hover:text-rose-900 pl-5" onClick={() => deleteButtonClick(user)}>Borrar</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      <nav
        className="flex items-center justify-between bg-white px-4 py-4 sm:px-6"
        aria-label="Pagination"
      >
        <div className="hidden sm:block">
          <p className="text-sm text-gray-700">
            Página <span className="font-medium">{currentPage}</span>, mostrando <span className="font-medium">{users.length}</span> resultados
          </p>
        </div>
        <div className="flex flex-1 justify-between sm:justify-end">
          <button
            disabled={currentPage === 1}
            onClick={handlePreviousPage}
            className='disabled:bg-gray-300 disabled:opacity-50 relative inline-flex items-center rounded-md bg-white px-3 py-3 text-sm font-semibold text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus-visible:outline-offset-0'
          >
            Anterior
          </button>
          <button
            disabled={isLastPage()}
            onClick={handleNextPage}
            className='disabled:bg-gray-300 disabled:opacity-50 relative ml-3 inline-flex items-center rounded-md bg-white px-3 py-3 text-sm font-semibold text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus-visible:outline-offset-0'
          >
            Siguiente
          </button>
        </div>
      </nav>
    </div>
  )
}
